import {
  AnimationCallbackEvent,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  inject,
} from "@angular/core";
import {CdkTrapFocus} from "@angular/cdk/a11y";
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  OverlayModule,
} from "@angular/cdk/overlay";
import {ScrollDispatcher} from "@angular/cdk/scrolling";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {MatTooltipModule} from "@angular/material/tooltip";
import {DiscussionReactionsState} from "../../services/discussion-reactions-state.service";
import {DiscussionEmoji} from "../../interfaces/discussion-reaction.interface";
import {DISCUSSION_EMOJIS} from "../../constants/discussion-reactions.constants";
import {DiscussionParticipant} from "../../interfaces/discussion.interface";

@Component({
  selector: "app-discussion-reactions",
  imports: [OverlayModule, TranslateModule, CdkTrapFocus, MatTooltipModule],
  templateUrl: "./discussion-reactions.component.html",
  styleUrl: "./discussion-reactions.component.css",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscussionReactionsComponent implements OnChanges {
  readonly state = inject(DiscussionReactionsState);
  private readonly exiting = new Set<Animation>();
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly scrollDispatcher = inject(ScrollDispatcher);
  private confirmation?: Animation;
  readonly emojis = DISCUSSION_EMOJIS;
  readonly names = [
    "GEM",
    "HAMSTER",
    "HEART",
    "SPARKLES",
    "LAUGH",
    "ANGRY",
    "THUMBS_UP",
  ];
  @Input({required: true}) targetKey!: string;
  @Input() participants: DiscussionParticipant[] = [];
  @Input() userId = "";
  @Input() enabled = false;
  @Input() ownMessage = false;
  @Input() concealed = false;
  @Input() actionsEnabled = false;
  @Output() closed = new EventEmitter<void>();
  @ViewChild("trigger") trigger?: ElementRef<HTMLButtonElement>;
  @ViewChild("actionsTrigger") actionsTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild("actionsOrigin") actionsOrigin?: CdkOverlayOrigin;
  @ViewChild("message") message?: ElementRef<HTMLElement>;
  @ViewChild("palette") palette?: ElementRef<HTMLElement>;
  @ViewChild("actions") actions?: ElementRef<HTMLElement>;
  hovered?: DiscussionEmoji;
  reactionsVisible = false;
  actionsVisible = false;
  private restoreActions = false;
  readonly positions: ConnectedPosition[] = [
    {
      originX: "center",
      originY: "top",
      overlayX: "center",
      overlayY: "bottom",
      offsetY: -8,
    },
    {
      originX: "center",
      originY: "bottom",
      overlayX: "center",
      overlayY: "top",
      offsetY: 8,
    },
  ];
  readonly actionPositions: ConnectedPosition[] = [
    {
      originX: "center",
      originY: "bottom",
      overlayX: "center",
      overlayY: "top",
      offsetY: 8,
    },
    {
      originX: "center",
      originY: "top",
      overlayX: "center",
      overlayY: "bottom",
      offsetY: -8,
    },
  ];
  private readonly ownActionPositions: ConnectedPosition[] = [
    {
      originX: "start",
      originY: "bottom",
      overlayX: "start",
      overlayY: "top",
      offsetY: 4,
    },
    {
      originX: "start",
      originY: "top",
      overlayX: "start",
      overlayY: "bottom",
      offsetY: -4,
    },
  ];
  private readonly receivedActionPositions: ConnectedPosition[] = [
    {
      originX: "end",
      originY: "bottom",
      overlayX: "end",
      overlayY: "top",
      offsetY: 4,
    },
    {
      originX: "end",
      originY: "top",
      overlayX: "end",
      overlayY: "bottom",
      offsetY: -4,
    },
  ];

  constructor() {
    this.destroyRef.onDestroy(() => {
      for (const animation of this.exiting) animation.cancel();
      this.exiting.clear();
      this.confirmation?.cancel();
    });
  }

  leave(event: AnimationCallbackEvent): void {
    const element = event.target;
    element.setAttribute("aria-hidden", "true");
    if (element instanceof HTMLElement) element.inert = true;
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) {
      event.animationComplete();
      return;
    }
    const animation = element.animate(
      [
        {opacity: 1, transform: "scale(1)"},
        {opacity: 0, transform: "scale(0.96)"},
      ],
      {duration: 100, easing: "ease-in"}
    );
    this.exiting.add(animation);
    const finish = () => {
      this.exiting.delete(animation);
      event.animationComplete();
    };
    void animation.finished.then(finish, finish);
  }

  ngOnChanges(): void {
    if (
      this.open &&
      ((!this.enabled && this.reactionsVisible) ||
        (!this.actionsEnabled && this.actionsVisible))
    )
      this.close(false);
  }

  get available(): boolean {
    return (
      !this.concealed &&
      this.state.view(this.targetKey)?.confirmed.status === "available"
    );
  }
  get open(): boolean {
    return this.state.active() === this.targetKey;
  }
  get reactions() {
    return this.state.view(this.targetKey)?.confirmed.reactions ?? [];
  }
  get actionAnchor(): CdkOverlayOrigin | undefined {
    return this.restoreActions ? this.actionsOrigin : undefined;
  }
  get menuPositions(): ConnectedPosition[] {
    if (!this.restoreActions) return this.actionPositions;
    return this.ownMessage
      ? this.ownActionPositions
      : this.receivedActionPositions;
  }

  show(withActions = true): void {
    this.reactionsVisible = this.enabled && this.available;
    this.actionsVisible = withActions && this.actionsEnabled;
    if (!this.reactionsVisible && !this.actionsVisible) return;
    this.restoreActions = false;
    if (withActions) this.revealContext();
    this.state.active.set(this.targetKey);
  }

  showActions(): void {
    if (!this.actionsEnabled) return;
    this.reactionsVisible = false;
    this.actionsVisible = true;
    this.restoreActions = true;
    this.state.active.set(this.targetKey);
  }

  private revealContext(): void {
    const message = this.message?.nativeElement;
    const viewport = message?.closest('[role="log"]')?.getBoundingClientRect();
    const bounds = message?.getBoundingClientRect();
    if (
      message &&
      bounds &&
      viewport &&
      (bounds.top < viewport.top + 64 || bounds.bottom > viewport.bottom - 144)
    ) {
      message.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "instant",
      });
    }
  }

  gem(): void {
    if (!this.enabled || !this.available) return;
    const alreadySelected = this.own("💎");
    this.state.choose(this.targetKey, "💎", true);
    if (
      !alreadySelected ||
      matchMedia("(prefers-reduced-motion: reduce)").matches
    )
      return;
    this.confirmation?.cancel();
    this.confirmation = this.element.nativeElement
      .querySelector('[data-reaction-emoji="💎"]')
      ?.animate(
        [
          {transform: "scale(1)"},
          {transform: "scale(1.15)"},
          {transform: "scale(1)"},
        ],
        {duration: 200}
      );
  }

  close(restore = true): void {
    this.hovered = undefined;
    if (this.open) this.state.active.set(null);
    if (restore) {
      const trigger = (this.restoreActions ? this.actionsTrigger : this.trigger)
        ?.nativeElement;
      const target =
        trigger && trigger.offsetWidth > 1
          ? trigger
          : this.message?.nativeElement;
      target?.focus({preventScroll: true});
    }
    this.closed.emit();
  }

  choose(emoji: DiscussionEmoji): void {
    if (!this.enabled || !this.available) return;
    this.state.choose(this.targetKey, emoji);
    this.close();
  }

  count(emoji: DiscussionEmoji): number {
    return this.reactions.filter((reaction) => reaction.emoji === emoji).length;
  }
  own(emoji: DiscussionEmoji): boolean {
    return this.reactions.some(
      (reaction) => reaction.emoji === emoji && reaction.userId === this.userId
    );
  }
  people(emoji: DiscussionEmoji): string {
    return this.reactions
      .filter((reaction) => reaction.emoji === emoji)
      .map(
        (reaction) =>
          this.participants.find((person) => person._id === reaction.userId)
            ?.username || this.translate.instant("DISCUSSION.DELETED_USER")
      )
      .join(", ");
  }

  keys(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      event.stopPropagation();
      this.close();
      return;
    }
    if (event.key === "Tab" && this.reactionsVisible && this.actionsVisible) {
      this.cycleContextFocus(event);
      return;
    }
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const buttons = Array.from(
      target
        .closest("[role=dialog]")
        ?.querySelectorAll<HTMLButtonElement>("[data-emoji]") ?? []
    );
    if (!buttons.length) return;
    const current = buttons.indexOf(target.closest("button")!);
    let next =
      (current + (event.key === "ArrowLeft" ? -1 : 1) + buttons.length) %
      buttons.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = buttons.length - 1;
    event.preventDefault();
    buttons[next]?.focus();
  }

  private cycleContextFocus(event: KeyboardEvent): void {
    const buttons = [this.palette, this.actions].flatMap((region) =>
      Array.from(
        region?.nativeElement.querySelectorAll<HTMLButtonElement>(
          "button:not(:disabled)"
        ) ?? []
      )
    );
    const index =
      event.target instanceof HTMLButtonElement
        ? buttons.indexOf(event.target)
        : -1;
    if (
      (event.shiftKey && index === 0) ||
      (!event.shiftKey && index === buttons.length - 1)
    ) {
      event.preventDefault();
      buttons[event.shiftKey ? buttons.length - 1 : 0]?.focus();
    }
  }

  attached(overlay: CdkConnectedOverlay): void {
    const strategy = overlay.overlayRef.getConfig().positionStrategy;
    if (strategy instanceof FlexibleConnectedPositionStrategy) {
      strategy.withScrollableContainers(
        this.scrollDispatcher.getAncestorScrollContainers(this.element)
      );
      overlay.overlayRef.updatePosition();
    }
    queueMicrotask(() => {
      if (!this.open || this.destroyRef.destroyed) return;
      const palette = this.reactionsVisible
        ? this.palette?.nativeElement
        : this.actions?.nativeElement;
      const button =
        palette?.querySelector<HTMLButtonElement>('[aria-pressed="true"]') ??
        palette?.querySelector("button");
      button?.focus({preventScroll: true});
    });
  }
}
